import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Search, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import type { RequestLog, ErrorLog, ConversationLog, ChatMessageLog } from '@/core/models';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

/** Parametri di deep-link, usati p.es. dalla card "Errori 7gg" della dashboard. */
interface LogsSearch {
  tab?: string;
  path?: string;
  statusCode?: string;
  startDate?: string;
  endDate?: string;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export const Route = createFileRoute('/logs')({
  head: () => ({
    meta: [{ title: 'Log · Replya Internal Backoffice' }],
  }),
  validateSearch: (search: Record<string, unknown>): LogsSearch => ({
    tab: optionalString(search.tab),
    path: optionalString(search.path),
    statusCode: optionalString(search.statusCode),
    startDate: optionalString(search.startDate),
    endDate: optionalString(search.endDate),
  }),
  component: LogsPage,
});

const LIMIT = 25;

function LogsPage() {
  const { selectedTenant } = useAppState();
  const search = Route.useSearch();
  const [tab, setTab] = useState(search.tab ?? 'requests');
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    path: search.path ?? '',
    statusCode: search.statusCode ?? '',
    startDate: search.startDate ?? '',
    endDate: search.endDate ?? '',
    conversationId: '',
  });
  const [conversationId, setConversationId] = useState<string | null>(null);

  const tenantId = selectedTenant?.tenantId ?? '';

  const commonParams = {
    limit: LIMIT,
    offset,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
  };

  const requestsQuery = useQuery({
    queryKey: ['requestLogs', tenantId, tab, filters, offset],
    queryFn: () =>
      apiService.getRequestLogs(tenantId, {
        ...commonParams,
        path: filters.path || undefined,
        statusCode: filters.statusCode ? Number(filters.statusCode) : undefined,
      }),
    enabled: !!selectedTenant && tab === 'requests',
  });

  const errorsQuery = useQuery({
    queryKey: ['errorLogs', tenantId, tab, filters, offset],
    queryFn: () =>
      apiService.getErrorLogs(tenantId, {
        ...commonParams,
        path: filters.path || undefined,
        statusCode: filters.statusCode ? Number(filters.statusCode) : undefined,
      }),
    enabled: !!selectedTenant && tab === 'errors',
  });

  const conversationsQuery = useQuery({
    queryKey: ['conversationLogs', tenantId, tab, filters, offset],
    queryFn: () => apiService.getConversationLogs(tenantId, commonParams),
    enabled: !!selectedTenant && tab === 'conversations',
  });

  const messagesQuery = useQuery({
    queryKey: ['chatMessageLogs', tenantId, tab, filters, offset],
    queryFn: () =>
      apiService.getChatMessageLogs(tenantId, {
        ...commonParams,
        conversationId: filters.conversationId || undefined,
      }),
    enabled: !!selectedTenant && tab === 'messages',
  });

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  const activeQuery =
    tab === 'requests'
      ? requestsQuery
      : tab === 'errors'
      ? errorsQuery
      : tab === 'conversations'
      ? conversationsQuery
      : messagesQuery;

  const data = activeQuery.data;
  const total = data?.total ?? 0;
  const hasNext = offset + LIMIT < total;
  const hasPrev = offset > 0;

  const requestLogs = tab === 'requests' ? (data?.items as RequestLog[] | undefined) : undefined;
  const errorLogs = tab === 'errors' ? (data?.items as ErrorLog[] | undefined) : undefined;
  const conversationLogs = tab === 'conversations' ? (data?.items as ConversationLog[] | undefined) : undefined;
  const messageLogs = tab === 'messages' ? (data?.items as ChatMessageLog[] | undefined) : undefined;

  const applyFilters = () => {
    setOffset(0);
    activeQuery.refetch();
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader title="Log operativi" subtitle={`Log e audit per ${selectedTenant.name}`} />

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setOffset(0); }}>
        <TabsList className="rounded-xl bg-surface-2 flex-wrap h-auto">
          <TabsTrigger value="requests" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Richieste
          </TabsTrigger>
          <TabsTrigger value="errors" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Errori
          </TabsTrigger>
          <TabsTrigger value="conversations" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Conversazioni
          </TabsTrigger>
          <TabsTrigger value="messages" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Messaggi
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="space-y-4 mt-4">
          <Card className="rounded-2xl border bg-surface">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                {(tab === 'requests' || tab === 'errors') && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Path</Label>
                      <Input
                        value={filters.path}
                        onChange={(e) => setFilters((f) => ({ ...f, path: e.target.value }))}
                        placeholder="/chat"
                        className="rounded-xl bg-surface-2 border-border h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Status</Label>
                      <Input
                        value={filters.statusCode}
                        onChange={(e) => setFilters((f) => ({ ...f, statusCode: e.target.value }))}
                        placeholder="500"
                        className="rounded-xl bg-surface-2 border-border h-9 w-24"
                      />
                    </div>
                  </>
                )}
                {tab === 'messages' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Conversation ID</Label>
                    <Input
                      value={filters.conversationId}
                      onChange={(e) => setFilters((f) => ({ ...f, conversationId: e.target.value }))}
                      placeholder="id conversazione"
                      className="rounded-xl bg-surface-2 border-border h-9"
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Dal</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                    className="rounded-xl bg-surface-2 border-border h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Al</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                    className="rounded-xl bg-surface-2 border-border h-9"
                  />
                </div>
                <Button onClick={applyFilters} className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground h-9">
                  <Search className="mr-2 h-4 w-4" />
                  Filtra
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border bg-surface">
            <CardHeader>
              <CardTitle className="text-base">
                Risultati {data ? `(${offset + 1}-${Math.min(offset + LIMIT, total)} di ${total})` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Caricamento...</p>
              ) : activeQuery.isError ? (
                <p className="text-sm text-destructive">
                  Errore: {(activeQuery.error as Error).message}
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto -mx-2 px-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {tab === 'requests' && (
                            <>
                              <TableHead>Data</TableHead>
                              <TableHead>Metodo</TableHead>
                              <TableHead>Path</TableHead>
                              <TableHead>Status</TableHead>
                            </>
                          )}
                          {tab === 'errors' && (
                            <>
                              <TableHead>Data</TableHead>
                              <TableHead>Path</TableHead>
                              <TableHead>Messaggio</TableHead>
                              <TableHead>Status</TableHead>
                            </>
                          )}
                          {tab === 'conversations' && (
                            <>
                              <TableHead>Data</TableHead>
                              <TableHead>ID</TableHead>
                              <TableHead>Summary</TableHead>
                              <TableHead></TableHead>
                            </>
                          )}
                          {tab === 'messages' && (
                            <>
                              <TableHead>Data</TableHead>
                              <TableHead>Role</TableHead>
                              <TableHead>Content</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data?.items.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="text-center text-muted-foreground"
                            >
                              Nessun risultato
                            </TableCell>
                          </TableRow>
                        )}
                        {tab === 'requests' &&
                          (requestLogs ?? []).map((log) => (
                            <TableRow key={log.id}>
                              <TableCell className="whitespace-nowrap text-xs tabular-nums">
                                {log.createdAt ? new Date(log.createdAt).toLocaleString('it-IT') : '—'}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {log.method}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-[240px] truncate">
                                {log.path}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={log.statusCode >= 400 ? 'destructive' : 'default'}
                                  className="text-xs"
                                >
                                  {log.statusCode}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        {tab === 'errors' &&
                          (errorLogs ?? []).map((log) => (
                            <TableRow key={log.id}>
                              <TableCell className="whitespace-nowrap text-xs tabular-nums">
                                {log.createdAt ? new Date(log.createdAt).toLocaleString('it-IT') : '—'}
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-[200px] truncate">
                                {log.path}
                              </TableCell>
                              <TableCell className="max-w-[300px] truncate text-xs">
                                {log.message}
                              </TableCell>
                              <TableCell>
                                {log.statusCode && (
                                  <Badge variant="destructive" className="text-xs">
                                    {log.statusCode}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        {tab === 'conversations' &&
                          (conversationLogs ?? []).map((log) => (
                            <TableRow key={log.id}>
                              <TableCell className="whitespace-nowrap text-xs tabular-nums">
                                {log.createdAt ? new Date(log.createdAt).toLocaleString('it-IT') : '—'}
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-[160px] truncate">
                                {log.conversationId}
                              </TableCell>
                              <TableCell className="text-xs">{log.summary ?? '—'}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-xl h-8"
                                  onClick={() => setConversationId(log.conversationId)}
                                >
                                  <MessageSquare className="mr-2 h-3 w-3" />
                                  Messaggi
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        {tab === 'messages' &&
                          (messageLogs ?? []).map((log) => (
                            <TableRow key={log.id}>
                              <TableCell className="whitespace-nowrap text-xs tabular-nums">
                                {log.createdAt ? new Date(log.createdAt).toLocaleString('it-IT') : '—'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={log.role === 'user' ? 'default' : 'secondary'} className="text-xs">
                                  {log.role}
                                </Badge>
                              </TableCell>
                              <TableCell className="max-w-[400px] truncate text-xs">
                                {log.content}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                      disabled={!hasPrev}
                    >
                      <ChevronLeft className="mr-2 h-4 w-4" />
                      Precedente
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Pagina {Math.floor(offset / LIMIT) + 1} di {Math.max(1, Math.ceil(total / LIMIT))}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => setOffset((o) => o + LIMIT)}
                      disabled={!hasNext}
                    >
                      Successiva
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={conversationId !== null} onOpenChange={(open) => !open && setConversationId(null)}>
        <DialogContent className="rounded-2xl border bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Messaggi conversazione</DialogTitle>
          </DialogHeader>
          {conversationId && (
            <ConversationMessages tenantId={selectedTenant.tenantId} conversationId={conversationId} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConversationMessages({ tenantId, conversationId }: { tenantId: string; conversationId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['conversationMessages', tenantId, conversationId],
    queryFn: () => apiService.getChatMessageLogs(tenantId, { conversationId, limit: 100 }),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Caricamento...</p>;
  if (!data?.items.length) return <p className="text-sm text-muted-foreground">Nessun messaggio.</p>;

  return (
    <div className="space-y-3">
      {data.items.map((msg) => (
        <div
          key={msg.id}
          className={`rounded-xl px-4 py-3 text-sm ${
            msg.role === 'user'
              ? 'ml-auto max-w-[80%] bg-brand text-brand-foreground'
              : 'max-w-[80%] bg-surface-2 text-foreground'
          }`}
        >
          <p className="text-[10px] uppercase tracking-wider opacity-80 mb-1">{msg.role}</p>
          <p className="whitespace-pre-wrap">{msg.content}</p>
        </div>
      ))}
    </div>
  );
}
